import { useState } from 'react';
import { useAuthForm } from '../../hooks/auth/useAuthForm';
import { useAuthSubmit } from '../../hooks/auth/useAuthSubmit';
import { useAuthModals } from '../../hooks/auth/useAuthModals';
import { useLoginRedirect } from '../../hooks/auth/useLoginRedirect';
import { LoginHero } from './LoginHero';
import { LoginFormContainer } from './LoginFormContainer';
import { EmailPasswordFields } from './EmailPasswordFields';
import { AuthModeToggle } from './AuthModeToggle';
import { AuthModalsGroup } from './AuthModalsGroup';
import { Button } from '../../components/Button';

/**
 * Login page component - refactored to separate business logic from UI.
 * All business logic is now in custom hooks, making it reusable for React Native.
 */
export const LoginPage = () => {
  // Web-specific redirect logic
  const redirect = useLoginRedirect();
  const [showMobileAuth, setShowMobileAuth] = useState(redirect.shouldStartInLoginMode);

  // Business logic hooks (platform-agnostic)
  const form = useAuthForm(!redirect.shouldStartInLoginMode); // Start in login mode if redirecting after auth
  const auth = useAuthSubmit();
  const modals = useAuthModals();

  // Derived content
  const title = form.isSignup ? 'Join the Adventure 🌴' : 'Welcome Back 👋';
  const subtitle = form.isSignup
    ? 'Start your journey with TripVibes today.'
    : 'Ready for your next trip?';
  const submitTitle = form.isSignup ? 'Create Account' : 'Log In';

  // Handler: Submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.canSubmit) return;

    const result = await auth.submit(form.email, form.password, form.isSignup);

    if (result.success) {
      redirect.redirectAfterSuccess();
    } else if (result.needsVerification) {
      modals.verification.open();
    } else if (result.inviteOnly) {
      modals.inviteOnly.open();
    } else {
      modals.failureNotification.open(result.error || 'Wrong username or password');
    }
  };

  // Handler: Login after verification
  const handleLoginAfterVerification = async () => {
    const result = await auth.submit(form.email, form.password, false);
    if (result.success) {
      redirect.redirectAfterSuccess();
    } else if (result.error) {
      modals.failureNotification.open(result.error);
    }
  };

  // Handler: Forgot password success
  const handleForgotPasswordSuccess = () => {
    modals.failureNotification.open(
      'Password reset successful! You can now log in with your new password.',
    );
  };

  return (
    <div className="flex min-h-screen bg-surface">
      {/* Overlay Logo */}
      <img
        className="absolute top-8 left-8 z-20 h-10 w-auto"
        src="/logo_side_full_big.png"
        alt="TripVibes"
        style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.4))' }}
      />

      {/* Left Column - Hero/Visuals */}
      <LoginHero
        showMobile={!showMobileAuth}
        onGetStarted={() => {
          form.setIsSignup(true);
          form.setPassword('');
          form.setPasswordRepeat('');
          setShowMobileAuth(true);
        }}
        onLogin={() => {
          form.setIsSignup(false);
          form.setPassword('');
          form.setPasswordRepeat('');
          setShowMobileAuth(true);
        }}
      />

      {/* Right Column - Form */}
      <LoginFormContainer
        title={title}
        subtitle={subtitle}
        showMobile={showMobileAuth}
        showBack={showMobileAuth}
        onBack={() => setShowMobileAuth(false)}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <EmailPasswordFields
            email={form.email}
            password={form.password}
            passwordRepeat={form.passwordRepeat}
            isSignup={form.isSignup}
            isProcessing={auth.isProcessing}
            passwordValid={form.passwordValid}
            onEmailChange={form.setEmail}
            onPasswordChange={form.setPassword}
            onPasswordRepeatChange={form.setPasswordRepeat}
            onPasswordValidChange={form.setPasswordValid}
            onForgotPasswordClick={modals.forgotPassword.open}
          />

          <div>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={!form.canSubmit || auth.isProcessing}
              loading={auth.isProcessing}
            >
              {auth.isProcessing ? 'Loading...' : submitTitle}
            </Button>
          </div>
        </form>

        <AuthModeToggle isSignup={form.isSignup} onToggle={form.toggleMode} />
      </LoginFormContainer>

      {/* Modals */}
      <AuthModalsGroup
        modals={modals}
        email={form.email}
        onLoginAfterVerification={handleLoginAfterVerification}
        onForgotPasswordSuccess={handleForgotPasswordSuccess}
      />
    </div>
  );
};
